'use client';

/**
 * PriceBooksPanel — the published price list as a versioned object.
 *
 * A price book holds everything the client pricing guide lists: plans (linked to
 * their plan tier), usage rates, add-ons, one-time fees, service packages,
 * migration tiers and cohort discounts. Published books are read-only: to change
 * a price, create a new version (a draft copy), edit it, and publish it — the
 * previous version is retired, and clients sold from it keep pointing at it.
 * The book's approval threshold decides when negotiated pricing needs a second
 * operator's approval.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, CopyPlus, Loader2, Save, Send, Archive, Pencil, X, FileText } from 'lucide-react';
import { openRevopsDocument } from './openRevopsDocument';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function headers(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}
async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${path}`, { ...init, headers: { ...headers(), ...(init.headers || {}) } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
  return data as T;
}

interface Book {
  id: string; code: string; name: string; version: number; status: 'draft' | 'published' | 'retired';
  effective_from: string | null; valid_through: string | null; approval_threshold_percent: number;
  item_count?: number; published_at?: string | null;
}
interface Item {
  id: string; item_type: string; code: string; name: string; client_label: string | null;
  pricing: Record<string, any>; tier_name?: string | null; sort_order: number;
}

const TYPE_LABEL: Record<string, string> = {
  plan: 'Plans', usage_rate: 'Usage rates', one_time: 'One-time fees', add_on: 'Add-ons',
  service_package: 'Custom work', migration_tier: 'Migration', cohort_discount: 'Sponsored cohorts',
};
const STATUS_CLS: Record<string, string> = {
  draft: 'bg-amber-50 text-amber-700', published: 'bg-green-50 text-green-700', retired: 'bg-gray-100 text-gray-500',
};

export default function PriceBooksPanel() {
  const [books, setBooks] = useState<Book[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<(Book & { items: Item[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<{ client_label: string; pricing: string }>({ client_label: '', pricing: '' });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadBooks = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api<{ price_books: Book[] }>('/price-books');
      setBooks(r.price_books);
      setSelected((cur) => cur || r.price_books.find((b) => b.status === 'published')?.id || r.price_books[0]?.id || null);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  const loadDetail = useCallback(async () => {
    if (!selected) { setDetail(null); return; }
    try { setDetail(await api(`/price-books/${selected}`)); } catch (e: any) { setError(e.message); }
  }, [selected]);
  useEffect(() => { loadBooks(); }, [loadBooks]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const run = async (key: string, fn: () => Promise<any>, done: string, select?: (r: any) => string | null) => {
    setBusy(key); setError(null); setNotice(null);
    try {
      const r = await fn();
      setNotice(done);
      await loadBooks();
      if (select) setSelected(select(r));
      else await loadDetail();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };

  const saveItem = (item: Item) => {
    let pricing: any;
    try { pricing = JSON.parse(form.pricing || '{}'); } catch { setError('Pricing must be valid JSON, e.g. {"amount": 5000}'); return; }
    run(`item:${item.id}`, () => api(`/price-books/${detail!.id}/items/${item.id}`, { method: 'PUT', body: JSON.stringify({ client_label: form.client_label, pricing }) }), 'Saved.')
      .then(() => setEditing(null));
  };

  const grouped = (detail?.items || []).reduce<Record<string, Item[]>>((acc, it) => { (acc[it.item_type] ||= []).push(it); return acc; }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><BookOpen className="h-5 w-5" /> Price books</h2>
          <p className="text-sm text-gray-500 mt-0.5 max-w-3xl">
            The published price list. To change a price, create a new version, edit the draft and publish it; the old version is
            retired and clients sold from it keep their prices. Discounts beyond the book's threshold need a second operator's approval.
          </p>
        </div>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {notice && !error && <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</div>}

      {loading ? (
        <div className="flex items-center text-sm text-gray-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading…</div>
      ) : books.length === 0 ? (
        <p className="text-sm text-gray-500">No price books yet.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <div className="space-y-1">
            {books.map((b) => (
              <button key={b.id} onClick={() => setSelected(b.id)} className={`w-full rounded-md border px-3 py-2 text-left text-sm ${selected === b.id ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{b.name}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[11px] ${STATUS_CLS[b.status]}`}>{b.status}</span>
                </div>
                <div className="text-xs text-gray-500">{b.code} · v{b.version} · {b.item_count ?? 0} items</div>
              </button>
            ))}
          </div>

          {detail && (
            <div className="min-w-0 rounded-md border border-gray-200 p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold">{detail.name} <span className="text-gray-500 font-normal">v{detail.version}</span></div>
                  <div className="text-xs text-gray-500">
                    {detail.effective_from && `Valid ${detail.effective_from} – ${detail.valid_through || 'open'} · `}
                    Approval needed above {detail.approval_threshold_percent}% discount
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {detail.status === 'draft' && (
                    <button onClick={() => run('publish', () => api(`/price-books/${detail.id}/publish`, { method: 'POST' }), `Published ${detail.code} v${detail.version}. The previous version is retired.`)}
                      disabled={!!busy} className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:bg-gray-400">
                      {busy === 'publish' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publish
                    </button>
                  )}
                  <button onClick={() => openRevopsDocument(`/price-books/${detail.id}/guide.html`).catch((e) => setError(e.message))}
                    className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200" title="Opens a printable pricing guide in a new tab">
                    <FileText className="h-4 w-4" /> Pricing guide
                  </button>
                  <button onClick={() => run('version', () => api(`/price-books/${detail.id}/new-version`, { method: 'POST' }), 'Created a draft copy. Edit it, then publish.', (r) => r.id)}
                    disabled={!!busy} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200">
                    <CopyPlus className="h-4 w-4" /> New version
                  </button>
                  {detail.status === 'published' && (
                    <button onClick={() => run('retire', () => api(`/price-books/${detail.id}/retire`, { method: 'POST' }), 'Retired.')}
                      disabled={!!busy} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200">
                      <Archive className="h-4 w-4" /> Retire
                    </button>
                  )}
                </div>
              </div>

              {Object.entries(TYPE_LABEL).filter(([t]) => grouped[t]?.length).map(([t, label]) => (
                <section key={t}>
                  <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-sm">
                      <tbody className="divide-y divide-gray-100">
                        {grouped[t].map((it) => (
                          <tr key={it.id} className="align-top">
                            <td className="py-1.5 pr-3 w-1/3">
                              <div className="font-medium">{it.name}</div>
                              {it.tier_name && <div className="text-xs text-gray-500">Plan tier: {it.tier_name}</div>}
                            </td>
                            {editing === it.id ? (
                              <td className="py-1.5" colSpan={2}>
                                <div className="space-y-1">
                                  <input id={`pb-label-${it.id}`} value={form.client_label} onChange={(e) => setForm((f) => ({ ...f, client_label: e.target.value }))} className="w-full rounded border border-gray-300 px-2 py-1 text-sm" placeholder="Shown to clients" />
                                  <textarea id={`pb-pricing-${it.id}`} value={form.pricing} onChange={(e) => setForm((f) => ({ ...f, pricing: e.target.value }))} rows={3} className="w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs" />
                                  <div className="flex gap-2">
                                    <button onClick={() => saveItem(it)} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline"><Save className="h-3 w-3" /> Save</button>
                                    <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:underline"><X className="h-3 w-3" /> Cancel</button>
                                  </div>
                                </div>
                              </td>
                            ) : (
                              <>
                                <td className="py-1.5 pr-3 text-gray-700">{it.client_label}</td>
                                <td className="py-1.5 text-right w-16">
                                  {detail.status === 'draft' && (
                                    <button onClick={() => { setEditing(it.id); setForm({ client_label: it.client_label || '', pricing: JSON.stringify(it.pricing, null, 0) }); }} className="text-gray-400 hover:text-indigo-600" title="Edit">
                                      <Pencil className="h-4 w-4" />
                                    </button>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
              {detail.status !== 'draft' && (
                <p className="text-xs text-gray-500">This version is {detail.status} and read-only. Use New version to change prices.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
